// src/services/itemsApi.js
import { EndpointBuilder } from "@reduxjs/toolkit/dist/query";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { baseURL } from "services/api";
import { Item } from "../item/types";
import {
  BookableListRequest,
  BusinessList,
  BusinessListDTO,
  ListRequest,
} from "./types";

const baseQuery = fetchBaseQuery({
  baseUrl: `${baseURL}/businesslist`,
  prepareHeaders: (headers) => {
    headers.set("Accept", "application/json");
    headers.set("Content-Type", "application/json");
    return headers;
  },
});

export const businessListApi = createApi({
  reducerPath: "businessListService",
  baseQuery,
  tagTypes: ["BusinessList"],
  endpoints: (builder: EndpointBuilder<any, any, any>) => ({
    getBusinessListById: builder.query<BusinessList, ListRequest>({
      query: (ListRequest: ListRequest) => ({
        url: `/${ListRequest.id}`,
        method: "GET",
      }),
    }),

    getBusinessListByUserId: builder.query<BusinessList[], ListRequest>({
      query: (ListRequest: ListRequest) => ({
        url: `/user/${ListRequest.id}`,
        method: "GET",
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: "BusinessList" as const,
                id,
              })),
              "BusinessList",
            ]
          : ["BusinessList"],
    }),

    getBookableItemsByIdAndDate: builder.query<Item[], BookableListRequest>({
      query: (BookableListRequest: BookableListRequest) => ({
        url: `/bookableItems/list/${BookableListRequest.id}/date/${BookableListRequest.date}`,
        method: "GET",
      }),
    }),

    getBookableDatesById: builder.query<string[], ListRequest>({
      query: (ListRequest: ListRequest) => ({
        url: `bookableDates/list/${ListRequest.id}`,
        method: "GET",
      }),
    }),

    getBusinessListNames: builder.query<BusinessList[], ListRequest>({
      query: (ListRequest: ListRequest) => ({
        url: `names/user/${ListRequest.id}`,
        method: "GET",
      }),
    }),

    createList: builder.mutation<any, BusinessListDTO>({
      query: (body: BusinessListDTO) => ({
        url: `/create`,
        method: "POST",
        body: JSON.stringify(body),
      }),
      invalidatesTags: ["BusinessList"],
    }),

    updateList: builder.mutation<any, { id: number; body: BusinessListDTO }>({
      query: ({ id, body }) => ({
        url: `/${id}`,
        method: "PUT",
        body: body,
      }),
      invalidatesTags: ["BusinessList"],
    }),
    deleteList: builder.mutation<any, { id: number }>({
      query: ({ id }) => ({
        url: `/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["BusinessList"],
    }),
  }),
});

export const {
  useGetBusinessListByIdQuery,
  useGetBusinessListByUserIdQuery,
  useLazyGetBookableItemsByIdAndDateQuery,
  useGetBookableDatesByIdQuery,
  useGetBusinessListNamesQuery,
  useCreateListMutation,
  useUpdateListMutation,
  useDeleteListMutation,
} = businessListApi;
